'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { use } from 'react';
import { trpc } from '@/app/_trpc/client';
import { DialogContext } from '@/components/features/dialogs/infra/context/DialogContext';
import Icon, { IconName } from '@/components/ui/Icon';
import {
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { MenuItemType } from './MenuConfig';
const MenuItem = ({
  item,
  onClick,
  isMobile = true,
  onClose,
  color,
}: {
  item: MenuItemType;
  onClick?: () => void;
  isMobile?: boolean;
  onClose?: () => void;
  color?: string;
}) => {
  const { setOpenMobile } = useSidebar();
  const { openDialog } = use(DialogContext);
  const t = useTranslations('menu');
  const { data: hasUnread } = trpc.messages.hasUnread.useQuery(undefined, {
    enabled: item.title === 'messages',
  });

  const pathname = usePathname();

  const isActive = item.link && pathname.startsWith(item.link);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (item.dialogKey) {
      openDialog(item.dialogKey, {});
      setOpenMobile(false);
    }
    if (onClose) onClose();
  };

  const commonClassName = cn(
    'flex items-center',
    isMobile
      ? 'gap-3'
      : 'w-full space-x-4 rounded-sm px-4 py-2 hover:bg-sidebar-accent',
    isActive && 'bg-white-300 md:bg-sidebar-accent'
  );

  const innerContent = (
    <>
      {item.title === 'messages' ? (
        <Icon variant={hasUnread ? IconName.MyChatOn : IconName.MyChatOff} />
      ) : (
        <Icon variant={item.icon} className={color && `bg-${color}`} />
      )}

      <div className={`text-left ${color && `text-${color}`}`}>
        <div
          className={`font-medium ${isMobile ? 'text-lg leading-4' : 'text-nowrap'}`}
        >
          {t(`${item.title}.title`)}
        </div>
        {t.has(`${item.title}.description`) && (
          <div className='text-muted-foreground text-xs'>
            {t(`${item.title}.description`)}
          </div>
        )}
      </div>
    </>
  );

  const menuContent = item.link ? (
    <Link
      href={item.link}
      onClick={() => {
        setOpenMobile(false);
        if (onClose) onClose();
      }}
      className={commonClassName}
      target={item.link.startsWith('http') ? '_blank' : undefined}
    >
      {innerContent}
    </Link>
  ) : (
    <div onClick={handleClick} className={`${commonClassName} cursor-pointer`}>
      {innerContent}
    </div>
  );

  if (isMobile) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          className={cn('h-auto py-2', isActive && 'bg-white-300')}
          onClick={onClick}
        >
          {menuContent}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return menuContent;
};
export default MenuItem;
